import React, {
  useState, useEffect, useCallback, useRef, useMemo, forwardRef,
} from 'react';
import PropTypes from 'prop-types';
import cns from 'classnames';
import Spinner from '../Spinner';
import PopOver from '../PopOver';
import useForwardRefOrRef from '../Hooks/useForwardRefOrRef';

const Button = forwardRef(({
  children,
  className,
  flat,
  floating,
  icon,
  primary,
  text,
  big,
  disabled,
  loading,
  selected,
  tooltip,
  onClick,
  ...others
}, ref) => {
  const classes = useMemo(() => cns(
    'button',
    {
      'button--primary': primary,
      'button--text': text,
      'button--flat': flat,
      'button--floating': floating,
      'button--big': big,
      'button--icon': icon,
      'button--standard': !flat && !floating && !icon,
      selected,
    },
    className,
  ), [big, className, flat, floating, icon, primary, selected, text]);

  const refButton = useForwardRefOrRef(ref);
  const [disabledInternal, setDisabled] = useState(disabled);
  const [running, setRunning] = useState(loading);

  useEffect(() => setDisabled(disabled), [disabled]);
  useEffect(() => setRunning(loading), [loading]);

  const onClickInternal = useCallback((e) => {
    if (disabledInternal || running || !onClick) {
      return;
    }
    refButton.current.blur();
    const res = onClick(e);
    if (res && (res.always || res.then)) {
      setDisabled(true);
      setRunning(true);
      if (res.always) {
        res.always(() => {
          setDisabled(false);
          setRunning(false);
        });
      } else {
        res.then(() => {
          setDisabled(false);
          setRunning(false);
        });
      }
    }
  }, [disabledInternal, onClick, refButton, running]);

  return (
    (floating || icon) && tooltip
      ? (
        <PopOver
          content={tooltip}
        >
          <button
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...others}
            className={classes}
            disabled={disabledInternal}
            onClick={onClickInternal}
            ref={refButton}
            type="button"
          >
            {children}
            {running && <span className="button__loader"><Spinner small /></span>}
          </button>
        </PopOver>
      )
      : (
        <button
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...others}
          className={classes}
          disabled={disabledInternal}
          onClick={onClickInternal}
          ref={refButton}
          type="button"
        >
          {children}
          {running && <span className="button__loader"><Spinner small /></span>}
        </button>
      )
  );
});

Button.propTypes = {
  /** Assegna lo stile big al bottone */
  big: PropTypes.bool,
  /** Contenuto da renderizzare all'interno del componente */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  /** Classi css personalizzate da aggiungere al componente */
  className: PropTypes.string,
  /** Indica se il componente è disabilitato */
  disabled: PropTypes.bool,
  /** Assegna lo stile flat al bottone */
  flat: PropTypes.bool,
  /** Assegna lo stile floating al bottone */
  floating: PropTypes.bool,
  /** Assegna lo stile icona al bottone */
  icon: PropTypes.bool,
  /** Indica se il pulsante è in caricamento */
  loading: PropTypes.bool,
  /**
Callback invocata al click sul componente (se viene passata una promise
allora viene attesa l'esecuzione con uno spinner):
```
 onClick(event)
```
dove:
* *evento*: evento scatenante

Se la callback ritorna una Promise, viene abilitata la gestione dello stato disposed
(disposed = true alla partenza, e disposed = false al then)

NOTA: Non viene invocata se sono a true le prop *disabled* oppure *loading*
   */
  onClick: PropTypes.func,
  /** Indica se il bottone è primario */
  primary: PropTypes.bool,
  /** Assegna lo stile selected al bottone */
  selected: PropTypes.bool,
  /** Assegna lo stile testuale al bottone */
  text: PropTypes.bool,
  /** Testo informativo visualizzato al hover del componente */
  tooltip: PropTypes.string,
};

Button.defaultProps = {
  big: false,
  children: null,
  className: '',
  disabled: false,
  flat: false,
  floating: false,
  icon: false,
  loading: false,
  onClick: null,
  primary: false,
  selected: false,
  text: false,
  tooltip: '',
};

export default Button;
